/**
 * #841: ONE client presence heartbeat for every session this browser tab has
 * open with a (possible) Allow/Deny or question card — not just the chat on
 * screen. The worker gives a card up ~65s after presence lapses (#540), and
 * presence used to be pinged only for the DISPLAYED chat, so switching chats
 * expired the other chat's card with the tab still open.
 *
 * Client→server only (the R6 rule: an SSE heartbeat must never keep presence
 * alive — the edge holds SSE open past tab close). Sources are functions read at
 * each tick, so no cross-key store subscription is needed; several mounted
 * surfaces registering the same ids still produce ONE request.
 */
import { request } from '@papan/api-client'

/** Matches the server's MAX_PRESENCE_IDS — a request over this is silently
 *  truncated there, so a union bigger than this must become several sends.
 *  A hand copy (the UI must not import the route module); parity is pinned by
 *  features/agent/api/__tests__/presence-route.test.ts. */
export const PRESENCE_BATCH = 50

export function createPresenceHeartbeat(opts: {
  send: (ids: string[]) => Promise<unknown>
  intervalMs: number
  setInterval?: typeof setInterval
  clearInterval?: typeof clearInterval
  setTimeout?: typeof setTimeout
  clearTimeout?: typeof clearTimeout
}) {
  const setI = opts.setInterval ?? setInterval
  const clearI = opts.clearInterval ?? clearInterval
  const setT = opts.setTimeout ?? setTimeout
  const clearT = opts.clearTimeout ?? clearTimeout
  // Reference-counted per source function. Today every registrant passes its OWN
  // closure (each useAgentSession instance its displayed-session ref — ChatPanel's and
  // HeroChat's both alive under keep-alive decks — and ChatPanel its open-tabs ref), so
  // each is a separate entry and one instance unmounting never drops another's ids.
  // The count keeps that true if a caller ever registers the SAME function twice (the
  // #841 fix-round-1 bug: an earlier shared module-level source, deduped by a Set, was
  // deleted by the first instance to unmount). A source is dropped, and the timer
  // stopped, only once every registration of it has been released.
  const sources = new Map<() => Iterable<string>, number>()
  let timer: ReturnType<typeof setInterval> | null = null
  // Coalesces several poke() calls landing in the same macrotask (e.g. two
  // useAgentSession instances both flipping to awaiting_permission off the
  // same SSE batch) into one scheduled tick, instead of one send per call.
  let pokeTimer: ReturnType<typeof setTimeout> | null = null

  const tick = () => {
    const ids = new Set<string>()
    for (const source of sources.keys()) {
      try { for (const id of source()) ids.add(id) } catch { /* one bad source never blocks the rest */ }
    }
    if (ids.size === 0) return
    const all = [...ids]
    // Chunk to PRESENCE_BATCH: a request over the server's MAX_PRESENCE_IDS
    // silently drops the extras there, so a bigger union must become several
    // sends rather than one truncated one.
    for (let i = 0; i < all.length; i += PRESENCE_BATCH) {
      void opts.send(all.slice(i, i + PRESENCE_BATCH)).catch(() => {})
    }
  }

  const poke = () => {
    if (pokeTimer) return // already scheduled — this call rides that one
    pokeTimer = setT(() => { pokeTimer = null; tick() }, 0)
  }

  return {
    register(source: () => Iterable<string>): () => void {
      sources.set(source, (sources.get(source) ?? 0) + 1)
      if (!timer) timer = setI(tick, opts.intervalMs)
      let released = false
      return () => {
        if (released) return // idempotent — a double-call must not over-decrement
        released = true
        const count = sources.get(source) ?? 0
        if (count <= 1) sources.delete(source)
        else sources.set(source, count - 1)
        if (sources.size === 0 && timer) { clearI(timer); timer = null }
        if (sources.size === 0 && pokeTimer) { clearT(pokeTimer); pokeTimer = null }
      }
    },
    poke,
    tick,
  }
}

export const presenceHeartbeat = createPresenceHeartbeat({
  send: (ids) => request('/api/agent/sessions/presence', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionIds: ids }),
  }),
  intervalMs: 5000,
})

/** A chat tab's session is pinged while it is busy: after a reload its card is not
 *  replayed into the store (attach is fire-and-forget), so "busy + open" is the
 *  only client-side signal that a card may be waiting there. */
export function openTabPresenceIds(tabIds: readonly string[], busyIds: ReadonlySet<string>): string[] {
  return tabIds.filter((id) => busyIds.has(id))
}

export function hasOpenCard(s: { status: string; pendingPermissions: readonly unknown[]; pendingQuestions: readonly unknown[] }): boolean {
  return s.status === 'awaiting_permission' || s.status === 'awaiting_answer'
    || s.pendingPermissions.length > 0 || s.pendingQuestions.length > 0
}
